import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

type StoredToken = { AccessToken?: string };

/**
 * Single option in either the `titles` or `currencies` dropdown.
 *
 * - `value` is the URL-safe string the BE expects back in query params
 *   (titles use underscores, e.g. `Cash_Deposit`).
 * - `label` is the human-readable text rendered in the dropdown.
 */
export type TransactionFilterOption = {
  value: string;
  label: string;
};

export type TransactionFilterDropdowns = {
  titles: TransactionFilterOption[];
  // The BE always returns `currencies` alongside `titles`. The
  // currencies picker isn't wired in `TransactionTable` yet — see
  // TODO(PAMENG-XXXX) at the consumption site. Keep the field on the
  // type so the follow-up doesn't have to widen the contract again.
  currencies: TransactionFilterOption[];
};

/** First segment of the `queryKey`; export so consumers can target invalidations. */
export const GET_TRANSACTIONS_FILTER_DROPDOWNS_QUERY_KEY =
  "GET_TRANSACTIONS_FILTER_DROPDOWNS";

const EMPTY_RESPONSE: TransactionFilterDropdowns = {
  titles: [],
  currencies: [],
};

function normalizeOption(raw: unknown): TransactionFilterOption | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    const v = raw.trim();
    return v ? { value: v, label: v } : null;
  }
  if (typeof raw === "object") {
    const r = raw as { value?: unknown; label?: unknown };
    const value = r.value != null ? String(r.value).trim() : "";
    if (!value) return null;
    const label = r.label != null ? String(r.label).trim() : value;
    return { value, label: label || value };
  }
  return null;
}

function normalizeList(raw: unknown): TransactionFilterOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeOption)
    .filter((o): o is TransactionFilterOption => Boolean(o));
}

/**
 * GET /admin-panel-users/v1/transactions/filter-dropdowns
 *
 * Returns the title + currency options for the user transactions table
 * filter row. `titles` is ordered by frequency (most common first); FE
 * sends the `value` field back as `transaction_titles` (comma-separated
 * for multi-select; single value for single-select).
 *
 * Tolerant of common BE envelopes (`{ data: { ... } }` vs flat) so the
 * dropdown never crashes on shape drift; falls back to empty arrays.
 *
 * `staleTime` is 5 minutes — these lists rarely change and BE derives
 * them dynamically from transaction data. Mirrors `useGetBetFilterDropdowns`.
 */
async function fetchTransactionFilterDropdowns(): Promise<TransactionFilterDropdowns> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await instance.get(
    API_ROUTES.GET_TRANSACTIONS_FILTER_DROPDOWNS,
  );
  const root = response?.data?.data ?? response?.data ?? null;
  if (!root || typeof root !== "object") return EMPTY_RESPONSE;

  const r = root as { titles?: unknown; currencies?: unknown };
  return {
    titles: normalizeList(r.titles),
    currencies: normalizeList(r.currencies),
  };
}

export function useGetTransactionFilterDropdowns() {
  return useQuery<TransactionFilterDropdowns, Error>({
    queryKey: [GET_TRANSACTIONS_FILTER_DROPDOWNS_QUERY_KEY],
    queryFn: fetchTransactionFilterDropdowns,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export default useGetTransactionFilterDropdowns;
